import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { logout } from 'features/auth/authenticationSlice';
import {
  changeBudgetScenarioName,
  deleteBudgetScenario,
  updateBudgetId
} from 'features/marketing-mix-modelling/budget-scenario-planner/edit-budget-scenario.slice';
import { clearSites, selectSite } from 'features/topbar/selectedSiteSlice';

export interface CampaignMmmControlState {
  selectedSegmentKey?: string;
  selectedTimeType?: string;
  selectedBudgetScenario:
    | { budgetId: string; scenarioName: string }
    | undefined;
  selectedModelRunDetails: {
    targetDate?: string;
    modelRunName?: string;
  };
}

const initialState: CampaignMmmControlState = {
  selectedSegmentKey: undefined,
  selectedTimeType: undefined,
  selectedBudgetScenario: undefined,
  selectedModelRunDetails: {
    targetDate: undefined,
    modelRunName: undefined
  }
};

const campaignMmmControlSlice = createSlice({
  name: 'campaignMmmControl',
  initialState,
  reducers: {
    setSelectedSegmentKey: (state, { payload }: { payload: string }) => {
      state.selectedSegmentKey = payload;
    },
    setSelectedTimeType: (state, { payload }: { payload: string }) => {
      state.selectedTimeType = payload;
    },
    setSelectedBudgetScenario: (state, action) => {
      state.selectedBudgetScenario = action.payload;
    },
    setSelectedModelRunDetails: (
      state,
      { payload }: { payload: { targetDate: string; modelRunName: string } }
    ) => {
      state.selectedModelRunDetails = payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase('PURGE', () => {
      return initialState;
    });
    builder.addCase(logout, () => {
      return initialState;
    });
    builder.addCase(selectSite, () => {
      return initialState;
    });
    builder.addCase(clearSites, () => {
      return initialState;
    });
    builder.addCase(
      deleteBudgetScenario,
      (state, action: PayloadAction<{ budgetId: string }>) => {
        const { budgetId } = action.payload;
        const isSelectedBudgetScenario =
          budgetId === state.selectedBudgetScenario?.budgetId;
        if (isSelectedBudgetScenario) {
          state.selectedBudgetScenario = undefined;
        }
      }
    );
    builder.addCase(changeBudgetScenarioName, (state, action) => {
      const { budgetId, scenarioName } = action.payload;

      const isSelectedBudgetScenario =
        budgetId === state.selectedBudgetScenario?.budgetId;

      const newBudgetId = updateBudgetId(budgetId, scenarioName);

      if (isSelectedBudgetScenario) {
        state.selectedBudgetScenario = {
          budgetId: newBudgetId,
          scenarioName
        };
      }
    });
  }
});

export const {
  setSelectedSegmentKey,
  setSelectedTimeType,
  setSelectedBudgetScenario,
  setSelectedModelRunDetails
} = campaignMmmControlSlice.actions;

export const campaignMmmControlReducer = campaignMmmControlSlice.reducer;

export const campaignMmmControlSliceName = campaignMmmControlSlice.name;

export const selectSelectedModelRunDetails = (state: {
  campaignMmmControl: CampaignMmmControlState;
}) => state.campaignMmmControl.selectedModelRunDetails;

export const selectSelectedSegmentKey = (state: {
  campaignMmmControl: CampaignMmmControlState;
}) => state.campaignMmmControl.selectedSegmentKey;

export const selectSelectedTimeType = (state: {
  campaignMmmControl: CampaignMmmControlState;
}) => state.campaignMmmControl.selectedTimeType;

export const selectSelectedBudgetScenario = createSelector(
  (state: RootState) => state.campaignMmmControl,
  (mmmControl) => mmmControl.selectedBudgetScenario
);

export const selectBudgetScenarioLabel = createSelector(
  (state: RootState) => state.campaignMmmControl,
  (mmmControl) => {
    return !mmmControl.selectedBudgetScenario
      ? 'Select Budget Scenario'
      : mmmControl.selectedBudgetScenario.scenarioName;
  }
);
